import { MarketplaceOpportunity } from '../models/opportunity';
import { MarketplaceOpportunityStatusList } from '../models/opportunity-status';
import { OpportunityState } from '../opportunity.state';
import { LocationTypes } from '../../timeline/models/enums/location-type.enum';
import { statesList } from '../../../../utils/locationsHelper';
import moment from 'moment';
import {
    getOtherFocus,
    isOtherFocus,
} from '../../../../containers/UserPages/Login/Modals/PracticeRegistrationForm/utils';
import { ServiceFocus } from '../../../service-focuses/models/service-focus';
import { isArchivedOpportunityStatus } from '../models/opportunity-status';
import { OpportunityStatusScope } from '../models/opportunities-table';

export const applyStatusScope = (
    opportunities: MarketplaceOpportunity[],
    statusScope: OpportunityStatusScope = 'active'
): MarketplaceOpportunity[] =>
    (opportunities ?? []).filter(
        (opportunity) =>
            isArchivedOpportunityStatus(opportunity.status) === (statusScope === 'archive')
    );

export const applyFilters = (
    opportunities: MarketplaceOpportunity[],
    filters: any,
    serviceFocuses: ServiceFocus[]
): MarketplaceOpportunity[] => {
    if (!opportunities || opportunities.length === 0) {
        return [];
    }

    let filtered = [...opportunities];

    Object.keys(filters).forEach((filterKey) => {
        const filterValue = filters[filterKey];

        if (
            !filterValue ||
            !Array.isArray(filterValue) ||
            filterValue.length === 0
        ) {
            return;
        }

        filtered = filtered.filter((opportunity) => {
            switch (filterKey) {
                case 'practices.state':
                    return opportunity.practices.some((practice) => {
                        const stateValue = practice.state;
                        if (!stateValue) return false;

                        const stateCode =
                            statesList.find(
                                (state) =>
                                    state.code.toLowerCase() ===
                                        stateValue.trim().toLowerCase() ||
                                    state.name.toLowerCase() ===
                                        stateValue.trim().toLowerCase()
                            )?.code || stateValue;

                        return filterValue.includes(stateCode);
                    });

                case 'status':
                    return filterValue.some(
                        (val: any) =>
                            Number(val) === opportunity.status ||
                            val === opportunity.status
                    );

                case 'engagementFteDvms':
                    const fteDvm = opportunity.engagementFteDvms;
                    const fteDvmStr = fteDvm?.toString() || 'NA';
                    return filterValue.some((selectedRange) => {
                        if (selectedRange === 'NA') {
                            return !fteDvm || fteDvmStr === 'NA';
                        }
                        return fteDvmStr === selectedRange.toString();
                    });

                case 'practices.practiceType':
                    return opportunity.practices.some((practice) => {
                        const practiceTypeShortLabel =
                            serviceFocuses.find(
                                (f) => f.id === practice.practiceType
                            )?.abbreviation ?? '';
                        return filterValue.includes(practiceTypeShortLabel);
                    });

                case 'practices.locationType':
                    return opportunity.practices.some((practice) => {
                        const locationTypeName = LocationTypes.find(
                            (f) => f.id === practice.locationType
                        )?.name;
                        return (
                            locationTypeName &&
                            filterValue.includes(locationTypeName)
                        );
                    });

                default:
                    return true;
            }
        });
    });

    return filtered;
};

export const applySorting = (
    opportunities: MarketplaceOpportunity[],
    sortOrder: any,
    serviceFocuses: ServiceFocus[]
): MarketplaceOpportunity[] => {
    if (!opportunities || opportunities.length === 0) {
        return [];
    }

    const sorted = [...opportunities];
    const { dataField, order } = sortOrder;

    sorted.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (dataField) {
            case 'listingDate':
                aValue = a.listingDate ? new Date(a.listingDate).getTime() : 0;
                bValue = b.listingDate ? new Date(b.listingDate).getTime() : 0;
                break;
            case 'opportunityId':
                aValue = a.opportunityId;
                bValue = b.opportunityId;
                break;
            case 'buyer.companyName':
                aValue = a.buyer?.companyName ?? '';
                bValue = b.buyer?.companyName ?? '';
                break;
            case 'engagementFteDvms':
                aValue = a.engagementFteDvms ?? 0;
                bValue = b.engagementFteDvms ?? 0;
                break;
            case 'status':
                aValue =
                    MarketplaceOpportunityStatusList.find(
                        (x) => x.id === a.status
                    )?.name ?? '';
                bValue =
                    MarketplaceOpportunityStatusList.find(
                        (x) => x.id === b.status
                    )?.name ?? '';
                break;
            case 'practices.practiceType':
                aValue = a.practices
                    .map((practice) => {
                        if (
                            isOtherFocus(serviceFocuses, practice.practiceType)
                        ) {
                            return (
                                practice.otherPracticeType ||
                                getOtherFocus(serviceFocuses)?.abbreviation ||
                                ''
                            );
                        }
                        return (
                            serviceFocuses.find(
                                (f) => f.id === practice.practiceType
                            )?.abbreviation ?? ''
                        );
                    })
                    .filter((f) => f)
                    .join(', ');
                bValue = b.practices
                    .map((practice) => {
                        if (
                            isOtherFocus(serviceFocuses, practice.practiceType)
                        ) {
                            return (
                                practice.otherPracticeType ||
                                getOtherFocus(serviceFocuses)?.abbreviation ||
                                ''
                            );
                        }
                        return (
                            serviceFocuses.find(
                                (f) => f.id === practice.practiceType
                            )?.abbreviation ?? ''
                        );
                    })
                    .filter((f) => f)
                    .join(', ');
                break;
            case 'practices.locationType':
                aValue =
                    a.practices
                        .map(
                            (practice) =>
                                LocationTypes.find(
                                    (f) => f.id === practice.locationType
                                )?.name ?? ''
                        )
                        .filter((f) => f)
                        .join(', ') || 'NA';
                bValue =
                    b.practices
                        .map(
                            (practice) =>
                                LocationTypes.find(
                                    (f) => f.id === practice.locationType
                                )?.name ?? ''
                        )
                        .filter((f) => f)
                        .join(', ') || 'NA';
                break;
            case 'practices.state':
                aValue =
                    a.practices
                        .filter((practice) => practice.state)
                        .map((practice) => practice.state)
                        .join(', ') || 'NA';
                bValue =
                    b.practices
                        .filter((practice) => practice.state)
                        .map((practice) => practice.state)
                        .join(', ') || 'NA';
                break;
            case 'currentEvent':
                aValue = a.currentEvent?.stageName ?? 'NA';
                bValue = b.currentEvent?.stageName ?? 'NA';
                break;
            case 'daysOnMarket':
                const calcDays = (opp: MarketplaceOpportunity) => {
                    const debut = opp.engagementData?.debutDate;
                    if (!debut) {
                        return null;
                    }
                    return moment().diff(moment(debut), 'days') + 1;
                };
                const aDays = calcDays(a);
                const bDays = calcDays(b);
                if (aDays === null && bDays === null) {
                    return 0;
                }
                if (aDays === null) {
                    return 1;
                }
                if (bDays === null) {
                    return -1;
                }
                aValue = aDays;
                bValue = bDays;
                break;
            default:
                aValue = (a as any)[dataField];
                bValue = (b as any)[dataField];
        }

        if (order === 'asc') {
            return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
            return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
    });

    return sorted;
};

export const getFilteredAndSortedOpportunities = (
    state: OpportunityState,
    serviceFocuses: ServiceFocus[] = []
): MarketplaceOpportunity[] => {
    let opportunities = applyStatusScope(
        state.opportunities.opportunities,
        state.opportunities.statusScope
    );
    opportunities = applyFilters(opportunities, state.opportunities.filters, serviceFocuses);
    opportunities = applySorting(opportunities, state.opportunities.sortOrder, serviceFocuses);
    return opportunities;
};

export const updateTableData = (state: OpportunityState, serviceFocuses: ServiceFocus[] = []) => {
    const filteredAndSorted = getFilteredAndSortedOpportunities(state, serviceFocuses);

    state.opportunities.pagination.totalSize = filteredAndSorted.length;

    const { page, sizePerPage } = state.opportunities.pagination;
    const startIndex = (page - 1) * sizePerPage;
    const endIndex = startIndex + sizePerPage;

    state.opportunities.data = filteredAndSorted.slice(startIndex, endIndex);
};
